import { Component, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, ElementRef, OnDestroy, NgZone } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IframeComponent, LoaderComponent, RouteChangeParams, RoutingService } from '@nts/std';
import { AuthService, PresentationCache } from '@nts/std';
import { LocalstorageHelper, LogService, TabSocket } from '@nts/std/src/lib/utility';
import { ModalService } from '@nts/std';
import { UUIDHelper } from '@nts/std/src/lib/utility';
import { concatMap, filter, map, startWith, Subject } from 'rxjs';
import { RouteAdditionalData } from '../../domain-models/route-additional-data';
import { RoutingHelper } from '../../../helper/routing-helper';
import { EnvironmentConfiguration } from '@nts/std/src/lib/environments';
import { NgIf } from '@angular/common';


@UntilDestroy()
@Component({
  selector: 'nts-remote-model-container',
  templateUrl: './remote-model-container.component.html',
  styleUrls: ['./remote-model-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    LoaderComponent,
    IframeComponent,
    NgIf
  ]
})
export class RemoteModelContainerComponent implements OnDestroy {

  private static readonly IFRAME_PARAM = 'iframe';
  private static readonly TRUE_VALUE = 'true';

  @ViewChild('iframe', { static: false }) iframe: ElementRef<HTMLIFrameElement>;

  url: string | null = null;
  currentEncodedAdditionalData: string = '';
  currentEncondedRootModelFullName: string = '';
  actionInProgress = false;

  private navigatorSocket: TabSocket;
  private tabMessagesSocket: TabSocket;
  private uuid = UUIDHelper.generateUUID();
  private onActionInProgress = new Subject<boolean>();
  private pendingRouteChecks = new Set<string>();

  constructor(
    private route: ActivatedRoute,
    private authService: AuthService,
    private modalService: ModalService,
    private router: Router,
    private cd: ChangeDetectorRef,
    private routingService: RoutingService,
    private routingHelper: RoutingHelper,
    private ngZone: NgZone,
    private environmentConfiguration: EnvironmentConfiguration
  ) {
    this.router.events
      .pipe(
        untilDestroyed(this),
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        startWith(null),
        map(() => ({
          encondedAdditionalData: this.route.snapshot.paramMap.get('encondedAdditionalData') ?? '',
          encondedRootModelFullName: this.route.snapshot.paramMap.get('encondedRootModelFullName') ?? ''
        })),
        filter((params) => this.markRouteCheckAsPending(params)),
        concatMap((params) => this.checkPendingRoute(params))
      )
      .subscribe();

    this.onActionInProgress.pipe(untilDestroyed(this)).subscribe((onActionInProgress) => {
      this.actionInProgress = onActionInProgress;
      this.cd.detectChanges();
    });
  }

  ngOnDestroy() {
    this.iframe = null;
    this.navigatorSocket?.destroy();
    this.navigatorSocket = null;
    this.tabMessagesSocket?.destroy();
    this.tabMessagesSocket = null;
    this.onActionInProgress?.complete();
    this.onActionInProgress = null;
    this.routingHelper?.unregisterForCurrentRoutePendingChanges(this.uuid);
  }

  private markRouteCheckAsPending(params: Params): boolean {
    const key = this.getRouteCheckKey(params);
    if (this.pendingRouteChecks.has(key)) {
      return false;
    }

    this.pendingRouteChecks.add(key);
    return true;
  }

  private async checkPendingRoute(params: Params): Promise<void> {
    try {
      await this.check(params);
    } finally {
      this.pendingRouteChecks.delete(this.getRouteCheckKey(params));
    }
  }

  private getRouteCheckKey(params: Params): string {
    return `${params['encondedRootModelFullName'] ?? ''}|${params['encondedAdditionalData'] ?? ''}`;
  }

  /**
   * Definisce le azioni da eseguire al caricamento dell'iframe.
   * @param iframeNativeElement
   */
  iframeLoading(iframeNativeElement: any): void {
    const tabMessagesSocket = this.createTabMessageSocket(iframeNativeElement.contentWindow);
    this.routingHelper.registerForCurrentRoutePendingChanges(this.uuid, tabMessagesSocket, this.modalService, this.onActionInProgress);
    this.navigatorSocketListen(iframeNativeElement, (params: RouteChangeParams) => {
      this.routingService.routeChangeRequested.next(params);
    }, 'navigator');
    LocalstorageHelper.startListener(iframeNativeElement.contentWindow);
  }

  /**
   * Controlla e gestisce i cambiamenti nei parametri della rotta.
   * @param res
   * @returns
   */
  private async check(res: Params): Promise<void> {
    if (!res) {
      return;
    }

    // Parse route parameters
    const { encodedAdditionalData, encodedRootModelFullName } = this.parseRouteParams(res);
    const routeContextChanged = this.hasRouteContextChanged(encodedAdditionalData, encodedRootModelFullName);

    if (routeContextChanged) {
      this.updateCurrentRouteParams(encodedAdditionalData, encodedRootModelFullName);
    }

    // Validate and decode model name
    const rootModelFullName = this.validateAndDecodeRootModelName(encodedRootModelFullName);
    if (!rootModelFullName) {
      this.router.navigate(['not-found']);
      return;
    }

    // Add to cache if route changed
    if (routeContextChanged) {
      await PresentationCache.addIfNotExist(rootModelFullName);
    }

    // Get presentation URL
    const returnedUrl = this.getPresentationUrl(rootModelFullName);
    if (!returnedUrl) {
      this.router.navigate(['not-found']);
      return;
    }

    // Build iframe URL
    const nextUrl = await this.buildIframeUrl(encodedAdditionalData, returnedUrl);

    // Handle URL update or reload
    this.handleUrlUpdate(routeContextChanged, nextUrl);
  }

  /**
   * Estrae i parametri della rotta dai parametri della query.
   * @param res
   * @returns
   */
  private parseRouteParams(res: Params): { encodedAdditionalData: string; encodedRootModelFullName: string; } {
    return {
      encodedAdditionalData: res['encondedAdditionalData'] ?? '',
      encodedRootModelFullName: res['encondedRootModelFullName'] ?? ''
    };
  }

  /**
   * Controlla se il contesto della rotta è cambiato confrontando i parametri codificati.
   * @param encodedAdditionalData
   * @param encodedRootModelFullName
   * @returns
   */
  private hasRouteContextChanged(encodedAdditionalData: string, encodedRootModelFullName: string): boolean {
    return this.currentEncodedAdditionalData !== encodedAdditionalData ||
      this.currentEncondedRootModelFullName !== encodedRootModelFullName;
  }

  /**
   * Aggiorna i parametri correnti della rotta.
   * @param encodedAdditionalData
   * @param encodedRootModelFullName
   */
  private updateCurrentRouteParams(encodedAdditionalData: string, encodedRootModelFullName: string): void {
    this.currentEncodedAdditionalData = encodedAdditionalData;
    this.currentEncondedRootModelFullName = encodedRootModelFullName;
  }

  /**
   * Valida e decodifica il nome completo del modello radice.
   * @param encodedRootModelFullName
   * @returns
   */
  private validateAndDecodeRootModelName(encodedRootModelFullName: string): string | null {
    return RoutingService.decodeUrl(encodedRootModelFullName);
  }

  /**
   * Ottiene l'URL di presentazione per un modello radice specificato.
   * @param rootModelFullName
   * @returns
   */
  private getPresentationUrl(rootModelFullName: string): string | null {
    return PresentationCache.get(rootModelFullName)?.replace(/([^:]\/)\/+/g, "$1") || null;
  }

  /**
   * Costruisce l'URL completo per l'iframe includendo i token di autenticazione e altri parametri.
   * @param encodedAdditionalData
   * @param returnedUrl
   * @returns
   */
  private async buildIframeUrl(encodedAdditionalData: string, returnedUrl: string): Promise<string> {
    const accessToken = await this.authService.getAccessToken();
    const refreshToken = await this.authService.getRefreshToken();
    const enterpriseData = await this.authService.getEnterpriseData(await this.authService.getTenantId());

    let additionalData = new RouteAdditionalData();
    try {
      additionalData = RoutingService.decodeObject<RouteAdditionalData>(encodedAdditionalData, RouteAdditionalData);
    } catch (e) {
      LogService.warn(`Impossibile decodificare ${encodedAdditionalData}`, e);
    }

    const searchParams = new URLSearchParams(additionalData.args ?? '');
    if (enterpriseData?.enterpriseId) {
      searchParams.set(AuthService.ENTERPRISE_ID_QUERY_KEY, enterpriseData.enterpriseId.toString());
    }
    if (enterpriseData?.companyId) {
      searchParams.set(AuthService.COMPANY_ID_QUERY_KEY, enterpriseData.companyId.toString());
    }
    searchParams.set(RemoteModelContainerComponent.IFRAME_PARAM, RemoteModelContainerComponent.TRUE_VALUE);
    searchParams.set(AuthService.REFRESH_TOKEN_QUERY_KEY, refreshToken);
    searchParams.set(AuthService.ACCESS_TOKEN_QUERY_KEY, accessToken);
    if (this.authService.cookiePreferences?.essentialCookies != null && this.authService.cookiePreferences?.analyticsCookies != null) {
      searchParams.set(AuthService.ESSENTIAL_COOKIES_QUERY_KEY, this.authService.cookiePreferences.essentialCookies.toString());
      searchParams.set(AuthService.ANALYTICS_COOKIES_QUERY_KEY, this.authService.cookiePreferences.analyticsCookies.toString());
    }

    if (additionalData.shortDescription?.length > 0) {
      window.document.title = `${additionalData.shortDescription} - ${this.environmentConfiguration.appTitle}`;
    }

    return `${returnedUrl}${additionalData?.encodedIdentity?.length > 0 ? '/' + additionalData.encodedIdentity : ''}?${searchParams.toString()}`;
  }

  /**
   * Gestisce l'aggiornamento o il ricaricamento dell'URL dell'iframe.
   * @param routeContextChanged
   * @param nextUrl
   * @returns
   */
  private handleUrlUpdate(routeContextChanged: boolean, nextUrl: string): void {
    if (!routeContextChanged) {
      this.forceIframeReload(nextUrl);
      return;
    }

    if (this.url === nextUrl) {
      this.forceIframeReload(nextUrl);
      return;
    }

    this.url = nextUrl;
    this.cd.markForCheck();
  }

  /**
   * Forza il ricaricamento dell'iframe anche se l'URL non è cambiato.
   * @param targetUrl
   * @returns
   */
  private forceIframeReload(targetUrl: string): void {
    // Non possiamo usare this.iframe.nativeElement.contentWindow.location.reload()
    // perché causa un errore CORS: "Blocked a frame with origin 'http://localhost:4200' from accessing a cross-origin frame."
    // Quindi forziamo la ricreazione dell'iframe togglando il binding Angular dell'URL.
    if (!targetUrl) {
      return;
    }

    const currentUrl = targetUrl;

    this.url = null;
    this.cd.markForCheck();

    setTimeout(() => {
      this.url = currentUrl;
      this.cd.markForCheck();
    });
  }

  /**
   * Ascolta i messaggi dal socket del navigatore.
   * @param openedWindow
   * @param listenAction
   * @param socketName
   */
  private navigatorSocketListen(
    openedWindow: Window,
    listenAction: (message: RouteChangeParams) => void,
    socketName: string
  ) {

    if (this.navigatorSocket) {
      this.navigatorSocket.destroy();
    }
    this.navigatorSocket = new TabSocket(this.ngZone, openedWindow, socketName);
    this.navigatorSocket.onClose.pipe(untilDestroyed(this)).subscribe(() => {
      this.navigatorSocket.destroy();
    });
    this.navigatorSocket.listen<RouteChangeParams>((message) => {
      // clearTimeout(timeout);
      listenAction(message.data);
    });
  }

  /**
   * Crea un socket per i messaggi della scheda.
   * @param openedWindow
   * @returns
   */
  private createTabMessageSocket(openedWindow: Window): TabSocket {
    if (this.tabMessagesSocket) {
      this.tabMessagesSocket.destroy();
    }

    this.tabMessagesSocket = new TabSocket(this.ngZone, openedWindow);
    return this.tabMessagesSocket;
  }
}
